import React, {useCallback, useEffect, useMemo, useState} from "react";
import useIsMounted from "../../../../hooks/use-is-mounted";
import Api from "../../../../../core/services/api/api";
import AutocompleteBase from "../autocomplete-base";
import {AutocompleteProps, TextFieldProps} from "@mui/material";
import {CancelToken} from "axios";

/**
 * Interface representing a part in the autocomplete dropdown
 * Defines the structure of each part option
 *
 * @property id - Unique identifier for the part
 * @property partNo - SalesPerson number/identifier string
 */
export interface SalesPersonOption {
    operatorId?: string;
    code: string;
    name?: string;
}

/**
 * Props interface for the SalesPersonAutocomplete component
 * Extends the base autocomplete functionality with part-specific features
 *
 * @property textFieldProps - Additional props to pass to the TextField component
 * @property form - Whether the component is used within a form
 * @property name - Name attribute for form integration
 * @property required - Whether the field is required in forms
 * @property setLoadingOptions - Callback to handle loading state changes
 * @property value - Currently selected part option
 * @property onChange - Callback fired when selection changes
 */
interface SalesPersonAutocompleteProps<M extends boolean = false> extends Omit<AutocompleteProps<SalesPersonOption, M, false, false>, 'renderInput' | 'renderOption' | 'options'> {
    textFieldProps?: Partial<TextFieldProps>;
    form?: boolean;
    name?: string;
    required?: boolean;
    setLoadingOptions?: (loading: boolean) => void;

    [key: string]: any;
}

/**
 * SalesPersonAutocomplete Component
 *
 * A searchable dropdown component for selecting parts with the following features:
 * - Industry-specific part filtering
 * - Async option loading with debouncing
 * - Loading state management
 * - Form integration support
 * - Customizable styling and behavior
 *
 * The component uses the autoCompleteSalesPersonLookup API to fetch parts based on:
 * - Search keywords (part number matching)
 * - Industry ID (optional filter)
 *
 * @component
 */
const SalesPersonAutocomplete = <M extends boolean = false, >({
                                                                  textFieldProps = {},
                                                                  form = false,
                                                                  name = undefined,
                                                                  required = false,
                                                                  setLoadingOptions: setLoadingOptionsProps,
                                                                  ...props
                                                              }: SalesPersonAutocompleteProps<M>) => {
    const isMounted = useIsMounted();
    const [loadingOptions, setLoadingOptions] = useState(false);

    /**
     * Syncs the internal loading state with the parent component
     */
    useEffect(() => {
        setLoadingOptionsProps?.(loadingOptions);
    }, [loadingOptions, setLoadingOptionsProps]);

    /**
     * Fetches part options based on search keywords and industry
     *
     * @param cancelToken - Axios cancel token for request cancellation
     * @param keywords - Search query string (matches against part numbers)
     * @returns Array of matching part options
     */
    const getOptions = useCallback(async (cancelToken: CancelToken, keywords?: string) => {
        setLoadingOptions(true);
        try {
            // @ts-ignore - API type definition is not complete
            const response = await Api.listSalesPersons(keywords ?? "", cancelToken);
            if (!isMounted())
                return [];
            return response?.data ?? [];
        } finally {
            if (isMounted()) {
                setLoadingOptions(false);
            }
        }
    }, [isMounted]);

    /**
     * Determines if two part options are equal based on their IDs
     */
    const isOptionEqualToValue = useCallback((option: SalesPersonOption, value: SalesPersonOption | null) =>
        option.code === value?.code, []);

    /**
     * Gets the display label for a part option (part number)
     */
    const getOptionLabel = useCallback((option: SalesPersonOption) => `${option?.code ?? ""}`, []);

    /**
     * Gets the unique key for a part option
     */
    const getOptionKey = useCallback((option: SalesPersonOption) => option?.code ?? "", []);

    // GetOptions will populate an initial empty array
    const defaultOptions = useMemo(() => [], []);

    return (
        <AutocompleteBase<SalesPersonOption, M>
            keywordsBased
            canSearchEmptyKeywords
            getOptions={getOptions}
            timeout={1000}
            textFieldProps={textFieldProps}
            form={form}
            name={name}
            required={required}
            isOptionEqualToValue={isOptionEqualToValue}
            getOptionLabel={getOptionLabel}
            getOptionKey={getOptionKey}
            options={defaultOptions}
            clearOnBlur={false}
            {...props}
        />
    );
};

export default SalesPersonAutocomplete;